import type { BroadcastMessage, WidgetConfig } from '@massimo/widget-protocol'

import { renderSafeMarkdown } from './markdown'
import { createDingPlayer } from './sound'
import { styles } from './styles'

interface RenderOptions {
  config: WidgetConfig
  onSend: (body: string) => Promise<{ ok: boolean; error?: string; messageId?: string }>
  onTyping: (isTyping: boolean) => void | Promise<void>
  onMessageReceived: (handler: (m: BroadcastMessage) => void) => void
  onStatusChanged: (handler: (s: 'open' | 'pending' | 'resolved' | 'closed') => void) => void
  onAgentTyping: (handler: (isTyping: boolean) => void) => void
}

interface DisplayMessage {
  id?: string
  kind: 'inbound' | 'outbound'
  body: string
  authorName: string
  createdAt: Date
  /** PLA-346 — AI zprávy renderují bezpečný markdown subset; ostatní plain text. */
  aiGenerated?: boolean
}

const TEXTS = {
  cs: {
    headerTitle: 'Jak pomůžeme?',
    headerSubtitle: 'Obvykle odpovídáme do hodiny.',
    placeholder: 'Napište zprávu…',
    send: 'Odeslat',
    welcomeMessage: 'Dobrý den! Napište nám, na co se chcete zeptat.',
    errorSending: 'Nepodařilo se odeslat. Zkuste to znovu.',
    you: 'Vy',
    support: 'Podpora',
    system: 'Systém',
    agent: 'Operátor',
    aiAssistant: 'AI asistent',
  },
  en: {
    headerTitle: 'How can we help?',
    headerSubtitle: 'We usually reply within an hour.',
    placeholder: 'Type a message…',
    send: 'Send',
    welcomeMessage: 'Hi! Send us a message — we’ll get back to you.',
    errorSending: 'Failed to send. Try again.',
    you: 'You',
    support: 'Support',
    system: 'System',
    agent: 'Agent',
    aiAssistant: 'AI assistant',
  },
}

interface WidgetController {
  open: () => void
  close: () => void
  /** Přepne stav (open↔close). Public API pro host page (PLA-15). */
  toggle: () => void
  /** Aktuální stav panelu (open=true). Public API pro host page (PLA-15). */
  isOpen: () => boolean
  hydrate: (messages: BroadcastMessage[]) => void
}

export function renderWidget(opts: RenderOptions): WidgetController {
  // Widget runtime narrowing — 'auto' nezná, embed.ts ji předem rezolvuje
  // přes navigator.language. Defensive cast pokud někdo zavolá renderWidget
  // bez rezoluce.
  const locale: 'cs' | 'en' = opts.config.locale === 'en' ? 'en' : 'cs'
  const defaults = TEXTS[locale]
  // Per-tenant overrides z /api/widget/config (resolved pro visitor locale).
  // Pokud server vrátí prázdný string nebo null, použij fallback z TEXTS.
  const t = {
    ...defaults,
    headerTitle: opts.config.title || defaults.headerTitle,
    headerSubtitle: opts.config.subtitle || defaults.headerSubtitle,
  }
  const primaryColor = opts.config.primaryColor ?? '#2563eb'

  // Host element + shadow DOM (zero CSS leak)
  const host = document.createElement('div')
  host.id = 'massimo-widget-host'
  const horizontalSide = opts.config.position === 'bottom-left' ? 'left' : 'right'
  host.style.cssText = `position:fixed;z-index:2147483647;bottom:24px;${horizontalSide}:24px;width:0;height:0;`
  document.body.appendChild(host)

  const shadow = host.attachShadow({ mode: 'open' })

  // Inject styles
  const styleEl = document.createElement('style')
  styleEl.textContent = styles({
    primaryColor,
    theme: opts.config.theme ?? 'light',
    position: opts.config.position ?? 'bottom-right',
  })
  shadow.appendChild(styleEl)

  // Build DOM
  const root = document.createElement('div')
  root.className = 'mw-root'
  const officeStatusHtml = opts.config.officeStatus
    ? `<div class="mw-office-status mw-office-${opts.config.officeStatus}" role="status">
         <span class="mw-office-dot" aria-hidden="true"></span>
         <span>${escapeHtml(opts.config.officeStatusMessage ?? '')}</span>
       </div>`
    : ''

  root.innerHTML = `
    <div class="mw-pane" hidden>
      <header class="mw-header">
        <div>
          <div class="mw-title">${t.headerTitle}</div>
          <div class="mw-subtitle">${t.headerSubtitle}</div>
          ${officeStatusHtml}
        </div>
        <button class="mw-close" aria-label="Close">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </header>
      <div class="mw-messages" role="log" aria-live="polite"></div>
      <div class="mw-typing" hidden aria-live="polite">
        <span class="mw-typing-dot"></span>
        <span class="mw-typing-dot"></span>
        <span class="mw-typing-dot"></span>
      </div>
      <form class="mw-composer">
        <textarea class="mw-input" placeholder="${t.placeholder}" rows="2"></textarea>
        <button class="mw-send" type="submit" aria-label="${t.send}">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="m22 2-11 11M22 2l-7 20-4-9-9-4Z"/>
          </svg>
        </button>
      </form>
    </div>
    <button class="mw-bubble" aria-label="Toggle chat">
      <svg class="mw-bubble-icon mw-bubble-icon-chat" viewBox="0 0 24 24" width="24" height="24" fill="white" aria-hidden="true">
        <path d="M2 21V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7l-5 3Z"/>
      </svg>
      <svg class="mw-bubble-icon mw-bubble-icon-close" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="white" stroke-width="2.5" aria-hidden="true" hidden>
        <path d="M18 6 6 18M6 6l12 12"/>
      </svg>
    </button>
  `
  shadow.appendChild(root)

  const bubble = root.querySelector<HTMLButtonElement>('.mw-bubble')!
  const bubbleIconChat = root.querySelector<SVGElement>('.mw-bubble-icon-chat')!
  const bubbleIconClose = root.querySelector<SVGElement>('.mw-bubble-icon-close')!
  const pane = root.querySelector<HTMLDivElement>('.mw-pane')!
  const closeBtn = root.querySelector<HTMLButtonElement>('.mw-close')!
  const messagesEl = root.querySelector<HTMLDivElement>('.mw-messages')!
  const typingEl = root.querySelector<HTMLDivElement>('.mw-typing')!
  const form = root.querySelector<HTMLFormElement>('.mw-composer')!
  const input = root.querySelector<HTMLTextAreaElement>('.mw-input')!

  const messages: DisplayMessage[] = []
  let welcomeShown = false
  const ding = createDingPlayer()
  const OPEN_KEY = 'massimo_widget_open'

  function readOpenState(): boolean {
    try {
      return localStorage.getItem(OPEN_KEY) === '1'
    } catch {
      return false
    }
  }
  function writeOpenState(open: boolean): void {
    try {
      localStorage.setItem(OPEN_KEY, open ? '1' : '0')
    } catch {
      /* storage blocked */
    }
  }

  function ensureWelcome() {
    if (welcomeShown) return
    welcomeShown = true
    appendMessage({
      kind: 'outbound',
      body: t.welcomeMessage,
      authorName: t.support,
      createdAt: new Date(),
    })
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight
    })
  }

  // Příchozí (outbound) zpráva může být dlouhá (AI odpověď). Místo skoku na úplný
  // konec — kde uživatel mine ZAČÁTEK zprávy — ukotvíme její horní okraj poblíž
  // vršku viewportu, takže se čte odshora a uživatel si doscrolluje dolů sám
  // (vzor ChatGPT/Claude). Scrollujeme jen vpřed (dolů), nikdy zpět.
  function scrollMessageStartIntoView(el: HTMLElement) {
    requestAnimationFrame(() => {
      const TOP_GAP = 8
      // Pozici počítáme RELATIVNĚ k scroll-kontejneru (messagesEl), ne přes
      // el.offsetTop — ten je vůči offsetParentu (pane vč. hlavičky), takže by
      // scroll přejel o výšku hlavičky. Delta = kolik px je horní okraj zprávy
      // pod viditelným vrškem seznamu; přičteme k aktuálnímu scrollTop.
      const delta = el.getBoundingClientRect().top - messagesEl.getBoundingClientRect().top
      const target = Math.max(0, messagesEl.scrollTop + delta - TOP_GAP)
      if (target > messagesEl.scrollTop) messagesEl.scrollTop = target
    })
  }

  function appendMessage(m: DisplayMessage) {
    messages.push(m)
    const el = document.createElement('div')
    el.className = `mw-msg mw-msg-${m.kind}`
    const iso = m.createdAt.toISOString()
    // PLA-346 — AI zprávy: bezpečný markdown subset (sanitizovaný HTML). Vlastní
    // (inbound), operátor, welcome/system/error = plain text přes escapeHtml.
    const bodyHtml = m.aiGenerated
      ? `<div class="mw-msg-bubble mw-msg-bubble-md">${renderSafeMarkdown(m.body)}</div>`
      : `<div class="mw-msg-bubble">${escapeHtml(m.body)}</div>`
    el.innerHTML = `
      ${bodyHtml}
      <div class="mw-msg-meta" data-timestamp="${iso}">${escapeHtml(m.kind === 'inbound' ? t.you : m.authorName)} · ${relativeTime(m.createdAt, locale)}</div>
    `
    messagesEl.appendChild(el)
    // Vlastní zpráva → scroll na konec (chci vidět, že odešla). Příchozí
    // (AI/agent/system) → ukotvit ZAČÁTEK nahoře, ať dlouhá odpověď nepřejede svůj
    // začátek; uživatel si dočte a doscrolluje dolů sám.
    if (m.kind === 'inbound') {
      scrollToBottom()
    } else {
      scrollMessageStartIntoView(el)
    }
  }

  // Auto-refresh všech relativních časů každou minutu. Cheaper než per-msg
  // interval — jeden timer prochází DOM s data-timestamp atributem.
  setInterval(() => {
    const metas = messagesEl.querySelectorAll<HTMLDivElement>('.mw-msg-meta[data-timestamp]')
    metas.forEach((meta) => {
      const ts = meta.dataset.timestamp
      if (!ts) return
      // Author name je první půlka před ' · ' — separator do labelu
      const authorPart = meta.textContent?.split(' · ')[0] ?? ''
      meta.textContent = `${authorPart} · ${relativeTime(new Date(ts), locale)}`
    })
  }, 60_000)

  function open(opts?: { persist?: boolean; focus?: boolean }) {
    pane.hidden = false
    bubbleIconChat.setAttribute('hidden', '')
    bubbleIconClose.removeAttribute('hidden')
    bubble.setAttribute('aria-label', 'Close chat')
    if (opts?.persist !== false) writeOpenState(true)
    // User gesture → unlock AudioContext pro budoucí dings (browser autoplay policy)
    ding.unlock()
    ensureWelcome()
    // Scroll na bottom (poslední zpráva) — důležité po hydrate kdy pane byl
    // hidden a scrollHeight === 0. Frame delay aby DOM dokončil layout.
    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight
    })
    if (opts?.focus !== false) setTimeout(() => input.focus(), 100)
  }

  function close() {
    pane.hidden = true
    bubbleIconChat.removeAttribute('hidden')
    bubbleIconClose.setAttribute('hidden', '')
    bubble.setAttribute('aria-label', 'Open chat')
    writeOpenState(false)
  }

  function toggle() {
    if (pane.hidden) open()
    else close()
  }

  bubble.addEventListener('click', toggle)
  closeBtn.addEventListener('click', close)

  // Unlock AudioContext při každém user gesture v rámci widgetu.
  // Po reloadu se widget může auto-open (persist state), ale to není user
  // gesture → browser autoplay policy blokuje AudioContext. Tahle redundance
  // ho odemkne při prvním reálném clicku/focusu kdekoliv ve widgetu.
  pane.addEventListener('click', () => ding.unlock(), true)
  input.addEventListener('focus', () => ding.unlock())

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    const body = input.value.trim()
    if (!body) return

    // Optimistic append
    appendMessage({
      kind: 'inbound',
      body,
      authorName: t.you,
      createdAt: new Date(),
    })
    input.value = ''

    const result = await opts.onSend(body)
    if (!result.ok) {
      appendMessage({
        kind: 'outbound',
        body: result.error ?? t.errorSending,
        authorName: t.system,
        createdAt: new Date(),
      })
    }
  })

  // Cmd+Enter or Enter to send (Shift+Enter = newline)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      form.requestSubmit()
    }
  })

  // Typing indicator — debounced. Fire 'true' once when user starts typing,
  // 'false' after 3s idle nebo po sendu. Throttle 'true' aby nešel POST každý keystroke.
  let typingTimer: ReturnType<typeof setTimeout> | null = null
  let typingActive = false
  function notifyTyping(isTyping: boolean) {
    if (isTyping === typingActive) return
    typingActive = isTyping
    void opts.onTyping(isTyping)
  }
  input.addEventListener('input', () => {
    if (input.value.length === 0) {
      notifyTyping(false)
      if (typingTimer) clearTimeout(typingTimer)
      return
    }
    notifyTyping(true)
    if (typingTimer) clearTimeout(typingTimer)
    typingTimer = setTimeout(() => notifyTyping(false), 3000)
  })

  // After successful send → reset typing state
  const origAppend = appendMessage
  // Wrap form.submit handler — we already do above, just patch typing in there:
  form.addEventListener('submit', () => {
    if (typingTimer) clearTimeout(typingTimer)
    notifyTyping(false)
  })

  // Agent typing indicator UI
  let agentTypingTimer: ReturnType<typeof setTimeout> | null = null
  opts.onAgentTyping((isTyping) => {
    if (isTyping) {
      typingEl.hidden = false
      // Auto-hide po 5s pokud server zapomene poslat false (safety)
      if (agentTypingTimer) clearTimeout(agentTypingTimer)
      agentTypingTimer = setTimeout(() => {
        typingEl.hidden = true
      }, 5000)
    } else {
      typingEl.hidden = true
      if (agentTypingTimer) clearTimeout(agentTypingTimer)
    }
    // Scroll na bottom aby typing indikátor byl viditelný
    requestAnimationFrame(() => {
      messagesEl.scrollTop = messagesEl.scrollHeight
    })
  })
  // Silence unused warning (origAppend is here for future optimistic patterns)
  void origAppend

  opts.onMessageReceived((m) => {
    appendMessage({
      id: m.id,
      kind: m.kind,
      body: m.body,
      authorName: localizeAuthorName(m.authorName, m.kind, m.aiGenerated, t),
      createdAt: new Date(m.createdAt),
      aiGenerated: m.aiGenerated,
    })
    // Zvuk jen pro agent/AI reply (outbound) — vlastní zprávy nemají
    // notification, ty visitor právě napsal.
    if (m.kind === 'outbound') ding.play()
  })

  opts.onStatusChanged(() => {
    // Future: vizualizovat status (resolved → "Tato konverzace byla vyřešena")
  })

  return {
    open,
    close,
    toggle,
    isOpen: () => !pane.hidden,
    hydrate(history) {
      // Po init z server-side state — overwrite welcome (pokud nebyl shown,
      // skip; pokud byl, neřeším — hydrate se volá před první open).
      welcomeShown = true // skip welcome — máme historii
      for (const m of history) {
        appendMessage({
          id: m.id,
          kind: m.kind,
          body: m.body,
          authorName: localizeAuthorName(m.authorName, m.kind, m.aiGenerated, t),
          createdAt: new Date(m.createdAt),
          aiGenerated: m.aiGenerated,
        })
      }
      // Restore last open state (persist napříč reloads).
      if (readOpenState()) {
        // focus=false: reload není user gesture, neukrast focus z host page
        open({ persist: false, focus: false })
      }
    },
  }
}

function escapeHtml(s: string): string {
  const div = document.createElement('div')
  div.textContent = s
  return div.innerHTML
}

type LocalizedTexts = (typeof TEXTS)['cs']

/**
 * Mapuje server-side authorName na visitor locale.
 *   - inbound (visitor's own message)        → t.you (Vy / You)
 *   - outbound + aiGenerated                  → t.aiAssistant
 *   - outbound se "Support"/"Agent" stringem  → t.support
 *   - jinak (jméno agenta jako "Maxim K.")    → použij jako-is (server poslal jméno)
 */
function localizeAuthorName(
  serverName: string,
  kind: 'inbound' | 'outbound',
  aiGenerated: boolean | undefined,
  t: LocalizedTexts,
): string {
  if (kind === 'inbound') return t.you
  if (aiGenerated) return t.aiAssistant
  if (serverName === 'Support' || serverName === 'Agent' || serverName === 'System') return t.support
  return serverName
}

function relativeTime(d: Date, locale: 'cs' | 'en' = 'cs'): string {
  const diffMs = Date.now() - d.getTime()
  const sec = Math.floor(diffMs / 1000)
  if (sec < 5) return locale === 'cs' ? 'teď' : 'just now'
  if (sec < 60) return locale === 'cs' ? `před ${sec} s` : `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return locale === 'cs' ? `před ${min} min` : `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return locale === 'cs' ? `před ${hr} h` : `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return locale === 'cs' ? `před ${day} d` : `${day}d ago`
  return d.toLocaleDateString(locale === 'cs' ? 'cs-CZ' : 'en-US', {
    day: 'numeric',
    month: 'short',
  })
}
